// Global
import { withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { useTheme } from 'lib/context/ThemeContext';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';

// Components
import { getEnum } from 'lib/utils/get-enum';
import { getHeadingLevel } from 'lib/utils/getHeadingLevel';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import Headline from 'src/helpers/Headline/Headline';
import MediaPrimary from 'src/helpers/Media/MediaPrimary';
import { HeroNavigationWithContainerTheme } from './HeroNavigationWithContainer.theme';
import { Subheadline } from 'src/helpers/Subheadline';
import { BodyCopy } from 'src/helpers/BodyCopy';
import { RichTextWrapper } from 'src/helpers/RichTextWrapper';
import { Placeholder } from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss/layout';
import React from 'react';

export type HeroNavigationWithContainerProps =
  Feature.EnterpriseWeb.RenewalByAndersen.Components.Hero.HeroNavigationWithContainer.HeroNavigationWithContainer & {
    rendering: ComponentRendering;
  };
const HeroNavigationWithContainer = (props: HeroNavigationWithContainerProps) => {
  const { rendering, fields } = props;
  const { themeData } = useTheme(HeroNavigationWithContainerTheme);
  const { currentScreenWidth } = useCurrentScreenType();

  if (!fields) {
    return null;
  }

  const isDesktop = currentScreenWidth >= getBreakpoint('md');
  const hasOverlay = !!getEnum(fields.overlay);

  return (
    <div
      data-component="hero/heronavigationwithcontainer"
      className="relative flex flex-col ml:w-full"
    >
      {hasOverlay && isDesktop && <div className={classNames(themeData.classes.darkOverlay)}></div>}
      <div className={classNames(themeData.classes.contentWrapper)}>
        <div className="mx-auto flex h-full w-full max-w-[78.125rem] flex-col md:flex-row">
          <div className="left-0 right-0 top-0 bottom-0 md:absolute">
            {/* Image */}
            <MediaPrimary
              imageLayout="fill"
              ratio="hero"
              additionalDesktopClasses="h-full"
              additionalMobileClasses="min-h-[17.5rem] md:order-0 order-2"
              priority
              {...props}
            />
          </div>
          <div className={classNames(themeData.classes.contentContainer)}>
            <Headline
              useTag={getHeadingLevel('h1', fields?.headlineLevel)}
              classes={classNames(themeData.classes.headline)}
              {...props}
            />
            <RichTextWrapper
              classes={classNames(themeData.classes.heroSubHeadlineText)}
              field={props.fields?.heroSubHeadlineText}
            />
            <Subheadline
              useTag={getHeadingLevel('h2', fields?.subheadlineLevel)}
              classes={classNames(themeData.classes.subHeadlineText)}
              {...props}
            />
            <BodyCopy classes={themeData.classes?.bodyClass} {...props} />
          </div>
          <div className={themeData.classes.componentContainerWrapper}>
            <div className={themeData.classes.componentContainer}>
              <Placeholder
                name="navComponents"
                rendering={rendering}
                render={(navComponents) =>
                  navComponents.map((navComponent, index) => (
                    <div key={index} className="component-placeholder--container-rail w-full">
                      {React.isValidElement(navComponent)
                        ? React.cloneElement(
                            navComponent as React.ReactElement<{ isInContainerRail?: boolean }>,
                            { isInContainerRail: true }
                          )
                        : navComponent}
                    </div>
                  ))
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withDatasourceCheck()<HeroNavigationWithContainerProps>(HeroNavigationWithContainer);
